import moment from "moment";
import "moment/locale/pt-br";
import { WxDayItem, WxSummaryData } from "./types";
import {
  MediasClimaticasPeriodo,
  MediasClimaticasResponse,
} from "./mediasClimaticas.types";
import { getDailyWeatherIcon } from "./getDailyWeatherIcon";
import { getWeatherIconSrc } from "./weatherIcons";
import {
  buildPlaceholderDays,
  buildPlaceholderSummary,
} from "./buildPlaceholderDays";

moment.locale("pt-br");

function sumChuvaMm(dias: MediasClimaticasPeriodo["dias"]): number | null {
  if (!dias?.length) return null;
  let total = 0;
  let hasValue = false;
  for (const dia of dias) {
    if (dia?.chuvaMm == null || Number.isNaN(Number(dia.chuvaMm))) continue;
    total += Number(dia.chuvaMm);
    hasValue = true;
  }
  return hasValue ? total : null;
}

function mapPeriodoSummary(periodo?: MediasClimaticasPeriodo | null): WxSummaryData {
  if (!periodo) return buildPlaceholderSummary();
  return {
    totalRainMm: sumChuvaMm(periodo.dias),
    avgTempC: periodo.temperaturaMediaC ?? null,
    avgHumidityPct: periodo.umidadeMediaPercentual ?? null,
  };
}

function mapPeriodoDays(
  periodo: MediasClimaticasPeriodo | null | undefined,
  kind: "ocorrido" | "previsao"
): WxDayItem[] {
  const dias = periodo?.dias;
  if (!dias?.length) {
    return buildPlaceholderDays(kind).map((day) => ({
      ...day,
      weatherKey: "clear-day",
      iconSrc: getWeatherIconSrc("clear-day"),
    }));
  }

  return [...dias]
    .filter((d) => !!d?.data)
    .sort((a, b) => moment(a.data).valueOf() - moment(b.data).valueOf())
    .map((d) => {
      const date = moment(d.data);
      const weatherKey = getDailyWeatherIcon(d.chuvaMm);
      return {
        date: date.format("DD/MM"),
        dayOfWeek: date.format("ddd").replace(".", ""),
        rainMm: d.chuvaMm ?? null,
        weatherKey,
        iconSrc: getWeatherIconSrc(weatherKey),
      };
    });
}

export type WxCardsViewModel = {
  ocorridoDays: WxDayItem[];
  previsaoDays: WxDayItem[];
  ocorridoSummary: WxSummaryData;
  previsaoSummary: WxSummaryData;
};

function emptyDays(kind: "ocorrido" | "previsao"): WxDayItem[] {
  return buildPlaceholderDays(kind).map((day) => ({
    ...day,
    rainMm: null,
    weatherKey: "clear-day",
    iconSrc: getWeatherIconSrc("clear-day"),
  }));
}

/** Layout estável com "-" quando não há dados / erro. */
export function mapMediasClimaticasToWx(
  data?: MediasClimaticasResponse | null
): WxCardsViewModel {
  if (!data) {
    return {
      ocorridoDays: emptyDays("ocorrido"),
      previsaoDays: emptyDays("previsao"),
      ocorridoSummary: buildPlaceholderSummary(),
      previsaoSummary: buildPlaceholderSummary(),
    };
  }

  return {
    ocorridoDays: mapPeriodoDays(data.ultimos7Dias, "ocorrido"),
    previsaoDays: mapPeriodoDays(data.proximos7Dias, "previsao"),
    ocorridoSummary: mapPeriodoSummary(data.ultimos7Dias),
    previsaoSummary: mapPeriodoSummary(data.proximos7Dias),
  };
}
